import * as i0 from '@angular/core';
import { Injectable, input, effect, Directive, computed, ChangeDetectionStrategy, Component, model, inject, DOCUMENT, NgModule } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { config, dom, icon, parse, counter, text } from '@fortawesome/fontawesome-svg-core';
const _c0 = ["*"];
let FaConfig = /*#__PURE__*/(() => {
  class FaConfig {
    /**
     * Default prefix to use, when one is not provided with the icon name.
     *
     * @default 'fas'
     */
    defaultPrefix = 'fas';
    /**
     * Provides a fallback icon to use whilst main icon is being loaded asynchronously.
     * When value is null, then fa-icon component will throw an error if icon input is missing.
     * When value is not null, then the provided icon will be used as a fallback icon if icon input is missing.
     *
     * @default null
     */
    fallbackIcon = null;
    /**
     * Set icons to the same fixed width.
     *
     * @see {@link: https://fontawesome.com/how-to-use/on-the-web/styling/fixed-width-icons}
     * @default false
     */
    fixedWidth;
    /**
     * Automatically add Font Awesome styles to the document when icon is rendered.
     *
     * For the majority of the cases the automatically added CSS is sufficient,
     * please refer to the linked guide for more information on when to disable
     * this feature.
     *
     * @see {@link: https://github.com/FortAwesome/angular-fontawesome/blob/main/docs/guide/adding-css.md}
     * @default true
     */
    set autoAddCss(value) {
      config.autoAddCss = value;
      this._autoAddCss = value;
    }
    get autoAddCss() {
      return this._autoAddCss;
    }
    _autoAddCss = true;
    static ɵfac = function FaConfig_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || FaConfig)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: FaConfig,
      factory: FaConfig.ɵfac,
      providedIn: 'root'
    });
  }
  return FaConfig;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FaIconLibrary = /*#__PURE__*/(() => {
  class FaIconLibrary {
    definitions = {};
    addIcons(...icons) {
      for (const icon of icons) {
        if (!(icon.prefix in this.definitions)) {
          this.definitions[icon.prefix] = {};
        }
        this.definitions[icon.prefix][icon.iconName] = icon;
        for (const alias of icon.icon[2]) {
          if (typeof alias === 'string') {
            this.definitions[icon.prefix][alias] = icon;
          }
        }
      }
    }
    addIconPacks(...packs) {
      for (const pack of packs) {
        const icons = Object.keys(pack).map(key => pack[key]);
        this.addIcons(...icons);
      }
    }
    getIconDefinition(prefix, name) {
      if (prefix in this.definitions && name in this.definitions[prefix]) {
        return this.definitions[prefix][name];
      }
      return null;
    }
    static ɵfac = function FaIconLibrary_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || FaIconLibrary)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: FaIconLibrary,
      factory: FaIconLibrary.ɵfac,
      providedIn: 'root'
    });
  }
  return FaIconLibrary;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const faWarnIfIconDefinitionMissing = iconSpec => {
  throw new Error(`Could not find icon with iconName=${iconSpec.iconName} and prefix=${iconSpec.prefix} in the icon library.`);
};
const faWarnIfIconSpecMissing = () => {
  throw new Error('Property `icon` is required for `fa-icon`/`fa-duotone-icon` components.');
};
const isKnownRotateValue = rotate => rotate != null && (rotate === 90 || rotate === 180 || rotate === 270 || rotate === '90' || rotate === '180' || rotate === '270');
/**
 * Fontawesome class list.
 * Returns classes array by props.
 */
const faClassList = props => {
  const knownRotateValue = isKnownRotateValue(props.rotate);
  const classes = {
    [`fa-${props.animation}`]: props.animation != null && !props.animation.startsWith('spin'),
    'fa-spin': props.animation === 'spin' || props.animation === 'spin-reverse',
    'fa-spin-pulse': props.animation === 'spin-pulse' || props.animation === 'spin-pulse-reverse',
    'fa-spin-reverse': props.animation === 'spin-reverse' || props.animation === 'spin-pulse-reverse',
    // According to https://fontawesome.com/docs/web/style/animate#spin fa-pulse
    // class is deprecated, remove the below line when Font Awesome 5 support
    // is dropped.
    'fa-pulse': props.animation === 'spin-pulse' || props.animation === 'spin-pulse-reverse',
    'fa-fw': props.fixedWidth,
    'fa-border': props.border,
    'fa-inverse': props.inverse,
    'fa-layers-counter': props.counter,
    'fa-flip-horizontal': props.flip === 'horizontal' || props.flip === 'both',
    'fa-flip-vertical': props.flip === 'vertical' || props.flip === 'both',
    [`fa-${props.size}`]: props.size !== null,
    [`fa-rotate-${props.rotate}`]: knownRotateValue,
    'fa-rotate-by': props.rotate != null && !knownRotateValue,
    [`fa-pull-${props.pull}`]: props.pull !== null,
    [`fa-stack-${props.stackItemSize}`]: props.stackItemSize != null
  };
  return Object.keys(classes).map(key => classes[key] ? key : null).filter(key => key != null);
};
const cssInserted = new WeakSet();
const autoCssId = 'fa-auto-css';
/**
 * Ensure that Font Awesome CSS is inserted into the page.
 *
 * SVG Core has the same logic to insert the same styles into the page, however
 * it's not aware of Angular SSR and therefore styles won't be added in that
 * context leading to https://github.com/FortAwesome/angular-fontawesome/issues/48.
 * That's why the same logic is duplicated here.
 *
 * @param document - Document.
 * @param config - Font Awesome configuration.
 */
function ensureCss(document, config) {
  if (!config.autoAddCss) {
    return;
  }
  if (cssInserted.has(document)) {
    return;
  }
  // Prevent adding the same styles again after hydration.
  if (document.getElementById(autoCssId) != null) {
    config.autoAddCss = false;
    cssInserted.add(document);
    return;
  }
  const style = document.createElement('style');
  style.setAttribute('type', 'text/css');
  style.setAttribute('id', autoCssId);
  style.innerHTML = dom.css();
  const headChildren = document.head.childNodes;
  let beforeChild = null;
  for (let i = headChildren.length - 1; i > -1; i--) {
    const child = headChildren[i];
    const tagName = child.nodeName.toUpperCase();
    if (['STYLE', 'LINK'].indexOf(tagName) > -1) {
      beforeChild = child;
    }
  }
  document.head.insertBefore(style, beforeChild);
  // Prevent SVG Core from adding the same styles.
  //
  // As the logic is present in two places and SVG Core is not aware about
  // this library, it may lead to styles being added twice. This can only
  // occur when icon is rendered by SVG Core before the Angular component
  // and should not have any significant negative impact. This is a rare
  // use case, and it's tricky to prevent, so we accept this behavior. Consumer
  // can choose to disable `FaConfig.autoAddCss` and add styles manually to
  // prevent this from happening.
  config.autoAddCss = false;
  cssInserted.add(document);
}

/**
 * Returns if is IconLookup or not.
 */
const isIconLookup = i => i.prefix !== undefined && i.iconName !== undefined;

/**
 * Normalizing icon spec.
 */
const faNormalizeIconSpec = (iconSpec, defaultPrefix) => {
  if (isIconLookup(iconSpec)) {
    return iconSpec;
  }
  if (Array.isArray(iconSpec) && iconSpec.length === 2) {
    return {
      prefix: iconSpec[0],
      iconName: iconSpec[1]
    };
  }
  return {
    prefix: defaultPrefix,
    iconName: iconSpec
  };
};
let FaStackItemSizeDirective = /*#__PURE__*/(() => {
  class FaStackItemSizeDirective {
    /**
     * Specify whether icon inside {@link FaStackComponent} should be rendered in
     * regular size (1x) or as a larger icon (2x).
     */
    stackItemSize = input('1x');
    /**
     * @internal
     */
    size = input();
    _effect = effect(() => {
      const size = this.size();
      if (size) {
        throw new Error('fa-icon is not allowed to customize size when used inside fa-stack. ' + 'Set size on the enclosing fa-stack instead: <fa-stack size="4x">...</fa-stack>.');
      }
    });
    static ɵfac = function FaStackItemSizeDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || FaStackItemSizeDirective)();
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: FaStackItemSizeDirective,
      selectors: [["fa-icon", "stackItemSize", ""], ["fa-duotone-icon", "stackItemSize", ""]],
      inputs: {
        stackItemSize: [1, "stackItemSize"],
        size: [1, "size"]
      }
    });
  }
  return FaStackItemSizeDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FaStackComponent = /*#__PURE__*/(() => {
  class FaStackComponent {
    /**
     * Size of the stacked icon.
     * Note that stacked icon is by default 2 times bigger, than non-stacked icon.
     * You'll need to set size using custom CSS to align stacked icon with a
     * simple one. E.g. `fa-stack { font-size: 0.5em; }`.
     */
    size = input();
    classes = computed(() => {
      const sizeValue = this.size();
      const sizeClass = sizeValue ? {
        [`fa-${sizeValue}`]: true
      } : {};
      return {
        ...sizeClass,
        'fa-stack': true
      };
    });
    static ɵfac = function FaStackComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || FaStackComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: FaStackComponent,
      selectors: [["fa-stack"]],
      hostVars: 2,
      hostBindings: function FaStackComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassMap(ctx.classes());
        }
      },
      inputs: {
        size: [1, "size"]
      },
      ngContentSelectors: _c0,
      decls: 1,
      vars: 0,
      template: function FaStackComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵprojection(0);
        }
      },
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return FaStackComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FaIconComponent = /*#__PURE__*/(() => {
  class FaIconComponent {
    icon = model.required();
    /**
     * Specify a title for the icon.
     *
     * This text will be displayed in a tooltip on hover and presented to the
     * screen readers.
     */
    title = model();
    /**
     * Icon animation.
     *
     * Most of the animations are only available when using Font Awesome 6. With
     * Font Awesome 5, only 'spin' and 'spin-pulse' are supported.
     */
    animation = model();
    mask = model();
    flip = model();
    size = model();
    pull = model();
    border = model();
    inverse = model();
    symbol = model();
    rotate = model();
    fixedWidth = model();
    transform = model();
    /**
     * Specify the `role` attribute for the rendered <svg> element.
     *
     * @default 'img'
     */
    a11yRole = model();
    renderedIconHTML = computed(() => {
      const iconValue = this.icon();
      if (iconValue == null && this.config.fallbackIcon == null) {
        faWarnIfIconSpecMissing();
        return '';
      }
      const iconDefinition = this.findIconDefinition(iconValue ?? this.config.fallbackIcon);
      if (!iconDefinition) {
        return '';
      }
      const params = this.buildParams();
      ensureCss(this.document, this.config);
      const renderedIcon = icon(iconDefinition, params);
      return this.sanitizer.bypassSecurityTrustHtml(renderedIcon.html.join('\n'));
    });
    document = inject(DOCUMENT);
    sanitizer = inject(DomSanitizer);
    config = inject(FaConfig);
    iconLibrary = inject(FaIconLibrary);
    stackItem = inject(FaStackItemSizeDirective, {
      optional: true
    });
    stack = inject(FaStackComponent, {
      optional: true
    });
    constructor() {
      if (this.stack != null && this.stackItem == null) {
        console.error('FontAwesome: fa-icon and fa-duotone-icon elements must specify stackItemSize attribute when wrapped into ' + 'fa-stack. Example: <fa-icon stackItemSize="2x"></fa-icon>.');
      }
    }
    findIconDefinition(i) {
      const lookup = faNormalizeIconSpec(i, this.config.defaultPrefix);
      if ('icon' in lookup) {
        return lookup;
      }
      const definition = this.iconLibrary.getIconDefinition(lookup.prefix, lookup.iconName);
      if (definition != null) {
        return definition;
      }
      faWarnIfIconDefinitionMissing(lookup);
      return null;
    }
    buildParams() {
      const fixedWidth = this.fixedWidth();
      const classOpts = {
        flip: this.flip(),
        animation: this.animation(),
        border: this.border(),
        inverse: this.inverse(),
        size: this.size(),
        pull: this.pull(),
        rotate: this.rotate(),
        fixedWidth: typeof fixedWidth === 'boolean' ? fixedWidth : this.config.fixedWidth,
        stackItemSize: this.stackItem != null ? this.stackItem.stackItemSize() : undefined
      };
      const transform = this.transform();
      const parsedTransform = typeof transform === 'string' ? parse.transform(transform) : transform;
      const mask = this.mask();
      const maskIconDefinition = mask != null ? this.findIconDefinition(mask) : null;
      const attributes = {};
      const a11yRole = this.a11yRole();
      if (a11yRole != null) {
        attributes['role'] = a11yRole;
      }
      const styles = {};
      if (classOpts.rotate != null && !isKnownRotateValue(classOpts.rotate)) {
        styles['--fa-rotate-angle'] = `${classOpts.rotate}`;
      }
      return {
        title: this.title(),
        transform: parsedTransform,
        classes: faClassList(classOpts),
        mask: maskIconDefinition ?? undefined,
        symbol: this.symbol(),
        attributes,
        styles
      };
    }
    static ɵfac = function FaIconComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || FaIconComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: FaIconComponent,
      selectors: [["fa-icon"]],
      hostAttrs: [1, "ng-fa-icon"],
      hostVars: 2,
      hostBindings: function FaIconComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵdomProperty("innerHTML", ctx.renderedIconHTML(), i0.ɵɵsanitizeHtml);
          i0.ɵɵattribute("title", ctx.title());
        }
      },
      inputs: {
        icon: [1, "icon"],
        title: [1, "title"],
        animation: [1, "animation"],
        mask: [1, "mask"],
        flip: [1, "flip"],
        size: [1, "size"],
        pull: [1, "pull"],
        border: [1, "border"],
        inverse: [1, "inverse"],
        symbol: [1, "symbol"],
        rotate: [1, "rotate"],
        fixedWidth: [1, "fixedWidth"],
        transform: [1, "transform"],
        a11yRole: [1, "a11yRole"]
      },
      outputs: {
        icon: "iconChange",
        title: "titleChange",
        animation: "animationChange",
        mask: "maskChange",
        flip: "flipChange",
        size: "sizeChange",
        pull: "pullChange",
        border: "borderChange",
        inverse: "inverseChange",
        symbol: "symbolChange",
        rotate: "rotateChange",
        fixedWidth: "fixedWidthChange",
        transform: "transformChange",
        a11yRole: "a11yRoleChange"
      },
      decls: 0,
      vars: 0,
      template: function FaIconComponent_Template(rf, ctx) {},
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return FaIconComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FaDuotoneIconComponent = /*#__PURE__*/(() => {
  class FaDuotoneIconComponent extends FaIconComponent {
    /**
     * Swap the default opacity of each duotone icon’s layers. This will make an
     * icon’s primary layer have the default opacity of 40% rather than its
     * secondary layer.
     *
     * @default false
     */
    swapOpacity = input();
    /**
     * Customize the opacity of the primary icon layer.
     * Valid values are in range [0, 1.0].
     *
     * @default 1.0
     */
    primaryOpacity = input();
    /**
     * Customize the opacity of the secondary icon layer.
     * Valid values are in range [0, 1.0].
     *
     * @default 0.4
     */
    secondaryOpacity = input();
    /**
     * Customize the color of the primary icon layer.
     * Accepts any valid CSS color value.
     *
     * @default CSS inherited color
     */
    primaryColor = input();
    /**
     * Customize the color of the secondary icon layer.
     * Accepts any valid CSS color value.
     *
     * @default CSS inherited color
     */
    secondaryColor = input();
    findIconDefinition(i) {
      const definition = super.findIconDefinition(i);
      if (definition != null && !Array.isArray(definition.icon[4])) {
        throw new Error('The specified icon does not appear to be a Duotone icon. ' + 'Check that you specified the correct style: ' + `<fa-duotone-icon [icon]="['fad', '${definition.iconName}']"></fa-duotone-icon> ` + `or use: <fa-icon icon="${definition.iconName}"></fa-icon> instead.`);
      }
      return definition;
    }
    buildParams() {
      const params = super.buildParams();
      const swapOpacity = this.swapOpacity();
      if (swapOpacity === true || swapOpacity === 'true') {
        if (Array.isArray(params.classes)) {
          params.classes.push('fa-swap-opacity');
        } else if (typeof params.classes === 'string') {
          params.classes = [params.classes, 'fa-swap-opacity'];
        } else {
          params.classes = ['fa-swap-opacity'];
        }
      }
      if (params.styles == null) {
        params.styles = {};
      }
      const primaryOpacity = this.primaryOpacity();
      if (primaryOpacity != null) {
        params.styles['--fa-primary-opacity'] = primaryOpacity.toString();
      }
      const secondaryOpacity = this.secondaryOpacity();
      if (secondaryOpacity != null) {
        params.styles['--fa-secondary-opacity'] = secondaryOpacity.toString();
      }
      const primaryColor = this.primaryColor();
      if (primaryColor != null) {
        params.styles['--fa-primary-color'] = primaryColor;
      }
      const secondaryColor = this.secondaryColor();
      if (secondaryColor != null) {
        params.styles['--fa-secondary-color'] = secondaryColor;
      }
      return params;
    }
    static ɵfac = /* @__PURE__ */(() => {
      let ɵFaDuotoneIconComponent_BaseFactory;
      return function FaDuotoneIconComponent_Factory(__ngFactoryType__) {
        return (ɵFaDuotoneIconComponent_BaseFactory || (ɵFaDuotoneIconComponent_BaseFactory = i0.ɵɵgetInheritedFactory(FaDuotoneIconComponent)))(__ngFactoryType__ || FaDuotoneIconComponent);
      };
    })();
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: FaDuotoneIconComponent,
      selectors: [["fa-duotone-icon"]],
      inputs: {
        swapOpacity: [1, "swapOpacity"],
        primaryOpacity: [1, "primaryOpacity"],
        secondaryOpacity: [1, "secondaryOpacity"],
        primaryColor: [1, "primaryColor"],
        secondaryColor: [1, "secondaryColor"]
      },
      features: [i0.ɵɵInheritDefinitionFeature],
      decls: 0,
      vars: 0,
      template: function FaDuotoneIconComponent_Template(rf, ctx) {},
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return FaDuotoneIconComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Warns if parent component not existing.
 */
const faWarnIfParentNotExist = (parent, parentName, childName) => {
  if (!parent) {
    throw new Error(`${childName} should be used as child of ${parentName} only.`);
  }
};

/**
 * Fontawesome layers.
 */
let FaLayersComponent = /*#__PURE__*/(() => {
  class FaLayersComponent {
    size = input();
    fixedWidth = input();
    faFw = computed(() => {
      const fixedWidth = this.fixedWidth();
      return typeof fixedWidth === 'boolean' ? fixedWidth : this.config.fixedWidth;
    });
    classes = computed(() => {
      const sizeValue = this.size();
      const sizeClass = sizeValue ? {
        [`fa-${sizeValue}`]: true
      } : {};
      return {
        ...sizeClass,
        'fa-fw': this.faFw(),
        'fa-layers': true
      };
    });
    document = inject(DOCUMENT);
    config = inject(FaConfig);
    ngOnInit() {
      ensureCss(this.document, this.config);
    }
    static ɵfac = function FaLayersComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || FaLayersComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: FaLayersComponent,
      selectors: [["fa-layers"]],
      hostVars: 2,
      hostBindings: function FaLayersComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassMap(ctx.classes());
        }
      },
      inputs: {
        size: [1, "size"],
        fixedWidth: [1, "fixedWidth"]
      },
      ngContentSelectors: _c0,
      decls: 1,
      vars: 0,
      template: function FaLayersComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵprojection(0);
        }
      },
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return FaLayersComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FaLayersCounterComponent = /*#__PURE__*/(() => {
  class FaLayersCounterComponent {
    content = input.required();
    title = input();
    position = input();
    renderedHTML = computed(() => {
      const params = this.buildParams();
      return this.updateContent(params);
    });
    document = inject(DOCUMENT);
    config = inject(FaConfig);
    parent = inject(FaLayersComponent, {
      optional: true
    });
    sanitizer = inject(DomSanitizer);
    constructor() {
      faWarnIfParentNotExist(this.parent, 'FaLayersComponent', this.constructor.name);
    }
    buildParams() {
      const position = this.position();
      return {
        title: this.title(),
        classes: position != null ? [`fa-layers-${position}`] : undefined
      };
    }
    updateContent(params) {
      ensureCss(this.document, this.config);
      return this.sanitizer.bypassSecurityTrustHtml(counter(this.content() || '', params).html.join(''));
    }
    static ɵfac = function FaLayersCounterComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || FaLayersCounterComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: FaLayersCounterComponent,
      selectors: [["fa-layers-counter"]],
      hostAttrs: [1, "ng-fa-layers-counter"],
      hostVars: 1,
      hostBindings: function FaLayersCounterComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵdomProperty("innerHTML", ctx.renderedHTML(), i0.ɵɵsanitizeHtml);
        }
      },
      inputs: {
        content: [1, "content"],
        title: [1, "title"],
        position: [1, "position"]
      },
      decls: 0,
      vars: 0,
      template: function FaLayersCounterComponent_Template(rf, ctx) {},
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return FaLayersCounterComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FaLayersTextComponent = /*#__PURE__*/(() => {
  class FaLayersTextComponent {
    content = input.required();
    title = input();
    flip = input();
    size = input();
    pull = input();
    border = input();
    inverse = input();
    rotate = input();
    fixedWidth = input();
    transform = input();
    renderedHTML = computed(() => {
      const params = this.buildParams();
      return this.updateContent(params);
    });
    document = inject(DOCUMENT);
    config = inject(FaConfig);
    parent = inject(FaLayersComponent, {
      optional: true
    });
    sanitizer = inject(DomSanitizer);
    constructor() {
      faWarnIfParentNotExist(this.parent, 'FaLayersComponent', this.constructor.name);
    }
    /**
     * Updating params by component props.
     */
    buildParams() {
      const classOpts = {
        flip: this.flip(),
        border: this.border(),
        inverse: this.inverse(),
        size: this.size(),
        pull: this.pull(),
        rotate: this.rotate(),
        fixedWidth: this.fixedWidth()
      };
      const transform = this.transform();
      const parsedTransform = typeof transform === 'string' ? parse.transform(transform) : transform;
      const styles = {};
      if (classOpts.rotate != null && !isKnownRotateValue(classOpts.rotate)) {
        styles['--fa-rotate-angle'] = `${classOpts.rotate}`;
      }
      return {
        transform: parsedTransform,
        classes: faClassList(classOpts),
        title: this.title(),
        styles
      };
    }
    updateContent(params) {
      ensureCss(this.document, this.config);
      return this.sanitizer.bypassSecurityTrustHtml(text(this.content() || '', params).html.join('\n'));
    }
    static ɵfac = function FaLayersTextComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || FaLayersTextComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: FaLayersTextComponent,
      selectors: [["fa-layers-text"]],
      hostAttrs: [1, "ng-fa-layers-text"],
      hostVars: 1,
      hostBindings: function FaLayersTextComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵdomProperty("innerHTML", ctx.renderedHTML(), i0.ɵɵsanitizeHtml);
        }
      },
      inputs: {
        content: [1, "content"],
        title: [1, "title"],
        flip: [1, "flip"],
        size: [1, "size"],
        pull: [1, "pull"],
        border: [1, "border"],
        inverse: [1, "inverse"],
        rotate: [1, "rotate"],
        fixedWidth: [1, "fixedWidth"],
        transform: [1, "transform"]
      },
      decls: 0,
      vars: 0,
      template: function FaLayersTextComponent_Template(rf, ctx) {},
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return FaLayersTextComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FontAwesomeModule = /*#__PURE__*/(() => {
  class FontAwesomeModule {
    static ɵfac = function FontAwesomeModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || FontAwesomeModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: FontAwesomeModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
  return FontAwesomeModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { FaConfig, FaDuotoneIconComponent, FaIconComponent, FaIconLibrary, FaLayersComponent, FaLayersCounterComponent, FaLayersTextComponent, FaStackComponent, FaStackItemSizeDirective, FontAwesomeModule };
